// UI-2: Toggle, Checkbox, Radio and SegmentedControl. design-brief.md "Toggles" and
// "Checkboxes"; the Add task modal's Priority segmented control. Each is the real native
// control (a checkbox, a radio) drawn over, so keyboard, forms and screen readers behave
// as they always do; the ring shows on the drawn box for keyboard focus.
import React, { useId, useRef } from 'react';
import { v } from './tokens';
import { PriorityDot } from './Priority';
import './v2.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// A switch: role="switch" on a checkbox. 38x22 track, label to the right.
export function Toggle({ checked, onChange, label, disabled = false, ...rest }) {
  return (
    <label className={cx('ui-check', 'ui-hit', disabled && 'ui-check--disabled')}>
      <input type="checkbox" role="switch" checked={!!checked} disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)} {...rest} />
      <span className="ui-toggle__track" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

export function Checkbox({ checked, indeterminate = false, onChange, label, disabled = false, ...rest }) {
  const ref = useRef(null);
  React.useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
  return (
    <label className={cx('ui-check', 'ui-hit', disabled && 'ui-check--disabled')}>
      <input ref={ref} type="checkbox" checked={!!checked} disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)} {...rest} />
      <span className="ui-check__box" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

// A group of native radios: arrows move and select, as browsers do. options: [{ value, label }]
export function RadioGroup({ options, value, onChange, label, name, disabled = false }) {
  const auto = useId();
  return (
    <fieldset className="ui-radios">
      {label && <legend className="ui-field__label">{label}</legend>}
      {options.map((o) => (
        <label key={o.value} className={cx('ui-check', 'ui-hit', (disabled || o.disabled) && 'ui-check--disabled')}>
          <input type="radio" name={name || auto} value={o.value} checked={value === o.value}
            disabled={disabled || o.disabled} onChange={() => onChange(o.value)} />
          <span className="ui-check__box ui-check__box--radio" aria-hidden="true" />
          <span>{o.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

// A row of mutually exclusive options (Low / Medium / High). A radiogroup: one tab stop,
// arrows move the choice. An option with a color (PRIORITY_OPTIONS, decision 50) fills the
// selected segment with it, keeping the charcoal outline; dot adds High's small red dot.
export function SegmentedControl({ options, value, onChange, label }) {
  const ref = useRef(null);
  const onKey = (e) => {
    const i = options.findIndex((o) => o.value === value);
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + options.length) % options.length;
    if (next === null) return;
    e.preventDefault();
    onChange(options[next].value);
    const buttons = ref.current?.querySelectorAll('[role="radio"]');
    setTimeout(() => buttons?.[next]?.focus(), 0);
  };
  return (
    <div ref={ref} role="radiogroup" aria-label={label} className="ui-segmented" onKeyDown={onKey}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
            className={`ui-segmented__option ui-hit${o.color ? ' ui-segmented__option--colored' : ''}`}
            style={o.color ? { '--ui-bg': v(o.color) } : undefined} onClick={() => onChange(o.value)}>
            {o.dot && <PriorityDot />}{o.label}
          </button>
        );
      })}
    </div>
  );
}
